import React, { useState, useRef, useEffect } from 'react';
import { Modal } from '../shared/Modal';
import { GameState } from '../../domain/entities';
import { DataService } from '../../services/DataService';
import { GAME_VERSION } from '../../version';
import { soundService } from '../../services/SoundService';
import { useToast } from '../../contexts/ToastContext';
import { SaveFile } from '../../infrastructure/GameStateRepository';
import './GameScreen.css';
import { GuideScreen } from './GuideScreen';
import { ThemeToggle } from '../shared/ThemeToggle';
import { FeedbackModal } from '../shared/FeedbackModal';
import { setThemeMode } from '../../theme';

interface GameScreenProps {
    gameState: GameState;
    onReturnToStart: () => void;
    onHardReset: () => void;
    onToggleDebug: () => void;
    listSaves: () => Promise<SaveFile[]>;
    onLoadNamedGame: (id: number) => Promise<boolean>;
    onSaveNamedGame: (id: number | null, name: string) => Promise<boolean>;
    onDeleteNamedGame: (id: number) => Promise<void>;
}

const FONT_OPTIONS = [
    { name: 'Modern Pixel (Default)', value: "'Silkscreen', sans-serif" },
    { name: 'Pixel', value: "'Press Start 2P', cursive" },
    { name: 'Retro', value: "'VT323', monospace" },
    { name: 'Mono Clean', value: "'Roboto Mono', monospace" },
    { name: 'Mono Code', value: "'Source Code Pro', monospace" },
    { name: 'Modern Sans', value: "'Inter', sans-serif" },
    { name: 'Clean Sans', value: "'Poppins', sans-serif" },
];
const FONT_SIZE_OPTIONS = [
    { name: 'Small', value: '10px' },
    { name: 'Medium (Default)', value: '12px' },
    { name: 'Large', value: '14px' },
];
const THEME_OPTIONS = [
    { name: 'Dark (Default)', value: 'dark' },
    { name: 'Light', value: 'light' },
    { name: 'Liquid Glass', value: 'liquid-glass' },
    { name: 'Dark Liquid Glass', value: 'dark-liquid-glass' },
    { name: 'Cyber Glass', value: 'dark-purple-liquid-glass' },
    { name: 'Cyan Neon', value: 'dark-cyan-neon' },
    { name: 'Green Neon', value: 'dark-green-neon' },
    { name: 'Synthwave', value: 'dark-synthwave' },
    { name: 'Soccer Cyber', value: 'soccer-cyber' },
    { name: 'Soccer Green', value: 'soccer-green' },
    { name: 'Soccer Green Liquid Glass', value: 'soccer-green-liquid-glass' },
];

export const GameScreen: React.FC<GameScreenProps> = ({ gameState, onReturnToStart, onHardReset, onToggleDebug, listSaves, onLoadNamedGame, onSaveNamedGame, onDeleteNamedGame }) => {
    const [modal, setModal] = useState<{ isOpen: boolean; type: 'new' | 'import' | 'reset' | null; title: string; content: string; }>({ isOpen: false, type: null, title: '', content: '' });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const clickTimestamps = useRef<number[]>([]);
    const { addToast } = useToast();
    
    const [settings, setSettings] = useState({
        theme: 'dark',
        font: FONT_OPTIONS[0].value,
        fontSize: FONT_SIZE_OPTIONS[1].value,
        sound: true,
    });

    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const [loadModalOpen, setLoadModalOpen] = useState(false);
    const [saves, setSaves] = useState<SaveFile[]>([]);
    const [saveName, setSaveName] = useState('');
    const [selectedSaveId, setSelectedSaveId] = useState<number | null>(null);
    const [showGuide, setShowGuide] = useState(false);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

    useEffect(() => {
        const savedTheme = localStorage.getItem('psm_theme') || 'dark';
        const savedFont = localStorage.getItem('psm_font') || FONT_OPTIONS[0].value;
        const savedFontSize = localStorage.getItem('psm_fontSize') || FONT_SIZE_OPTIONS[1].value;
        
        setSettings({
            theme: savedTheme,
            font: savedFont,
            fontSize: savedFontSize,
            sound: soundService.getIsEnabled(),
        });
    }, []);

    const handleSettingsChange = (type: 'theme' | 'font' | 'fontSize' | 'sound', value: string | boolean) => {
        if (type !== 'sound') {
            localStorage.setItem(`psm_${type}`, String(value));
        }

        const newSettings = {...settings, [type]: value};
        setSettings(newSettings as any);

        if (type === 'theme') {
            setThemeMode(value as any);
        } else if (type === 'font') {
            document.documentElement.style.setProperty('--font-family', String(value));
        } else if (type === 'fontSize') {
            document.documentElement.style.setProperty('--font-size', String(value));
        } else if (type === 'sound') {
            soundService.toggleSound(Boolean(value));
        }
        soundService.playClick();
    };

    const openModal = (type: 'new' | 'import' | 'reset', title: string, content: string) => {
        soundService.playClick();
        setModal({ isOpen: true, type, title, content });
    }

    const handleConfirm = () => {
        soundService.playSuccess();
        if (modal.type === 'new') {
            onReturnToStart();
        } else if (modal.type === 'import') {
            fileInputRef.current?.click();
        } else if (modal.type === 'reset') {
            onHardReset();
        }
        setModal({ isOpen: false, type: null, title: '', content: '' });
    };

    const handleExport = (gameType: 'mens' | 'womens') => {
        soundService.playClick();
        DataService.exportBaseData(gameType);
        addToast(`${gameType === 'mens' ? "Men's" : "Women's"} base data exported!`, 'info');
    };

    const handleExportGameState = async () => {
        soundService.playClick();
        await DataService.exportFullGameState(gameState);
        addToast('Game state exported!', 'success');
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
       const file = event.target.files?.[0];
       if (file) {
           try {
               const data = await DataService.importData(file);
               // This function is not available here, but the user is taken back to the start screen where they can import.
               // We will keep the button but change the functionality to just go back.
               addToast('Custom data imported successfully! You can now start a new game with it.', 'success');
           } catch (error: any) {
               soundService.playError();
               addToast(`Import failed: ${error.message}`, 'error');
           }
       }
       if (fileInputRef.current) {
           fileInputRef.current.value = '';
       }
   };

    const handleVersionClick = () => {
        const now = Date.now();
        clickTimestamps.current = clickTimestamps.current.filter(ts => now - ts < 1500);
        clickTimestamps.current.push(now);

        if (clickTimestamps.current.length >= 4) {
            soundService.playSuccess();
            addToast('Debug mode toggled!', 'info');
            onToggleDebug();
            clickTimestamps.current = [];
        }
    };
    
    const handleOpenSaveModal = async () => {
        soundService.playClick();
        const savesList = await listSaves();
        setSaves(savesList);
        setSaveName(`S${gameState.season}W${Math.floor(gameState.currentWeek)} - ${gameState.playerTeamName}`);
        setSelectedSaveId(null);
        setSaveModalOpen(true);
    };

    const handleSaveGame = async () => {
        soundService.playClick();
        if (!saveName.trim()) {
            addToast('Please enter a save name', 'error');
            return;
        }
        const success = await onSaveNamedGame(selectedSaveId, saveName.trim());
        if (success) {
            addToast('Game saved!', 'success');
            setSaveModalOpen(false);
        } else {
            addToast('Failed to save game.', 'error');
        }
    };

    const handleOpenLoadModal = async () => {
        soundService.playClick();
        const savesList = await listSaves();
        setSaves(savesList);
        setLoadModalOpen(true);
    };

    const handleLoadGame = async (id: number) => {
        soundService.playClick();
        if (window.confirm('Loading this game will overwrite your current session. Are you sure?')) {
            const success = await onLoadNamedGame(id);
            if (success) {
                addToast('Game loaded successfully!', 'success');
                setLoadModalOpen(false);
            } else {
                addToast('Failed to load game.', 'error');
            }
        }
    };
    
    const handleDeleteGame = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        soundService.playClick();
        if (window.confirm('Are you sure you want to permanently delete this save?')) {
            await onDeleteNamedGame(id);
            addToast('Save deleted!', 'info');
            const savesList = await listSaves();
            setSaves(savesList);
        }
    };

    if (showGuide) {
        return (
            <GuideScreen onBack={() => {
                soundService.playClick();
                setShowGuide(false);
            }} />
        );
    }

    return (
        <>
            <div className="panel">
                <div className="panel-header">Game Guide</div>
                <div className="panel-content">
                    <p style={{marginBottom: '16px'}}>Need a refresher on game mechanics or want to learn the basics?</p>
                    <button 
                        className="button button-guide" 
                        onClick={() => {
                            soundService.playClick();
                            setShowGuide(true);
                        }} 
                        style={{width: '100%'}}
                    >
                        Read the Guide
                    </button>
                </div>
            </div>

            <div className="panel">
                <div className="panel-header">Game Options</div>
                <div className="panel-content">
                    <p style={{marginBottom: '16px'}}>Manage your game session.</p>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                            <button className="button" onClick={handleOpenSaveModal} style={{minWidth: '180px'}}>Save Game</button>
                            <span>Game is saved automatically after each week. Use this to create manual save points.</span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                            <button className="button" onClick={handleOpenLoadModal} style={{minWidth: '180px'}}>Load Game</button>
                            <span>Load a previously saved game.</span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                            <button className="button" onClick={handleExportGameState} style={{minWidth: '180px'}}>Export Game</button>
                            <span>Export your current game to a file. Useful for bug reports.</span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                            <button className="button danger" onClick={() => openModal('new', 'Start New Game?', 'This will erase your current autosave and return you to the main menu. Are you sure?')} style={{minWidth: '180px'}}>Start New Game</button>
                            <span className="negative">Warning: This will delete your current progress.</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="panel">
                <div className="panel-header">Settings</div>
                <div className="panel-content">
                    <p style={{marginBottom: '16px'}}>Customize the look and feel of the game. Your preferences are saved automatically.</p>
                    <div className="settings-grid">
                        <div className="form-group">
                            <label htmlFor="theme-select">Theme</label>
                            <select id="theme-select" value={settings.theme} onChange={e => handleSettingsChange('theme', e.target.value)}>
                                {THEME_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.name}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="font-select">Font</label>
                            <select id="font-select" value={settings.font} onChange={e => handleSettingsChange('font', e.target.value)}>
                                {FONT_OPTIONS.map(opt => <option key={opt.value} value={opt.value} style={{fontFamily: opt.value}}>{opt.name}</option>)}
                            </select>
                        </div>
                         <div className="form-group">
                            <label htmlFor="fontsize-select">Font Size</label>
                            <select id="fontsize-select" value={settings.fontSize} onChange={e => handleSettingsChange('fontSize', e.target.value)}>
                                {FONT_SIZE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.name}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="sound-toggle">Enable Sound</label>
                            <input type="checkbox" id="sound-toggle" checked={settings.sound} onChange={e => handleSettingsChange('sound', e.target.checked)} />
                        </div>
                    </div>
                    <div style={{marginTop: '24px', paddingTop: '16px', borderTop: '1px solid rgba(var(--accent-color-rgb), 0.2)'}}>
                        <p style={{marginBottom: '16px', fontWeight: 600}}>Select Theme:</p>
                        <ThemeToggle onThemeChange={() => {
                            const newTheme = localStorage.getItem('psm_theme') || 'dark';
                            setSettings({...settings, theme: newTheme});
                        }} />
                    </div>
                </div>
            </div>

            <div className="panel">
                <div className="panel-header">Feedback</div>
                <div className="panel-content">
                    <p style={{marginBottom: '16px'}}>Found a bug? Have a feature suggestion? We'd love to hear from you!</p>
                    <button
                        className="button button-feedback"
                        onClick={() => {
                            soundService.playClick();
                            setIsFeedbackModalOpen(true);
                        }}
                        style={{width: '100%'}}
                    >
                        Send Feedback
                    </button>
                </div>
            </div>

            <div className="panel">
                 <div className="panel-header">Custom Data</div>
                 <div className="panel-content">
                     <p style={{marginBottom: '16px'}}>
                        Export the base game world to a JSON file to edit team/player names. 
                        You can import a file to start a new career from the main menu.
                    </p>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'}}>
                            <button className="button secondary" onClick={() => handleExport('mens')} style={{minWidth: '220px'}}>Export Men's Base Data</button>
                            <button className="button secondary" onClick={() => handleExport('womens')} style={{minWidth: '220px'}}>Export Women's Base Data</button>
                        </div>
                    </div>
                 </div>
            </div>

            <div className="panel" style={{ borderLeftColor: 'var(--negative-color)' }}>
                <div className="panel-header" style={{ color: 'var(--negative-color)' }}>Danger Zone</div>
                <div className="panel-content">
                    <p style={{marginBottom: '16px'}}>Permanently delete all saved data and settings. This cannot be undone.</p>
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <button className="button danger" onClick={() => openModal('reset', 'Reset All Game Data?', 'This will permanently delete your saved game and all settings from your browser. This action cannot be undone. Are you sure?')} style={{minWidth: '200px'}}>Reset All Data</button>
                    </div>
                </div>
            </div>

            <div className="panel">
                <div className="panel-header">Support The Game</div>
                <div className="panel-content">
                    <p style={{marginBottom: '16px'}}>
                        If you're enjoying Pixel Soccer Manager, consider supporting its development!
                    </p>
                    <a href="https://www.buymeacoffee.com/richie5um" target="_blank" rel="noopener noreferrer">
                        <img src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png" alt="Buy Me A Coffee" style={{ height: '60px', width: '217px' }} />
                    </a>
                     <p 
                        style={{textAlign: 'right', opacity: 0.7, marginTop: '24px', userSelect: 'none'}}
                        onClick={handleVersionClick}
                        title="What are you clicking at?"
                     >
                        Version: {GAME_VERSION}
                     </p>
                </div>
            </div>

            <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} accept=".json" />

            <Modal isOpen={modal.isOpen} onClose={() => setModal({isOpen: false, type: null, title: '', content: ''})}>
                <h2>{modal.title}</h2>
                <p>{modal.content}</p>
                <div className="modal-actions">
                    <button className="button" onClick={() => setModal({isOpen: false, type: null, title: '', content: ''})}>Cancel</button>
                    <button className={`button ${modal.type === 'reset' ? 'danger' : 'primary'}`} onClick={handleConfirm}>Confirm</button>
                </div>
            </Modal>

            <Modal isOpen={saveModalOpen} onClose={() => setSaveModalOpen(false)}>
                <h2>Save Game</h2>
                <div className="form-group">
                    <label htmlFor="save-name-input">Save Name</label>
                    <input id="save-name-input" type="text" value={saveName} onChange={e => setSaveName(e.target.value)} />
                </div>
                <h4>Overwrite existing save:</h4>
                <div className="save-list">
                    {saves.map(save => (
                        <button key={save.id} className="button" onClick={() => { setSaveName(save.name); setSelectedSaveId(save.id); }}>{save.name}</button>
                    ))}
                </div>
                <div className="modal-actions">
                    <button className="button" onClick={() => setSaveModalOpen(false)}>Cancel</button>
                    <button className="button primary" onClick={handleSaveGame}>Save</button>
                </div>
            </Modal>

            <Modal isOpen={loadModalOpen} onClose={() => setLoadModalOpen(false)}>
                <h2>Load Game</h2>
                <div className="table-wrapper">
                    {saves.length > 0 ? (
                        <table className="table">
                             <thead>
                                <tr>
                                    <th>Save</th>
                                    <th>Details</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {saves.map(save => (
                                    <tr key={save.id} className="clickable-row" onClick={() => handleLoadGame(save.id)}>
                                        <td>{save.name}<br /><small>{new Date(save.timestamp).toLocaleString()}</small></td>
                                        <td>{save.playerTeamName}<br />S{save.season} W{save.currentWeek}</td>
                                        <td>
                                            <button className="button small danger" onClick={(e) => handleDeleteGame(e, save.id)}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <p>No named saves found.</p>}
                </div>
                 <div className="modal-actions">
                    <button className="button" onClick={() => setLoadModalOpen(false)}>Cancel</button>
                </div>
            </Modal>

            <FeedbackModal isOpen={isFeedbackModalOpen} onClose={() => setIsFeedbackModalOpen(false)} />
        </>
    );
};